import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Fab,
  Zoom,
  OutlinedInput,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SecurityIcon from '@mui/icons-material/Security';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from 'react-router-dom';
import {
  useAuth,
  DriverStatus,
  DayOfWeek,
  UserRole,
  SDSUser,
  DELIVERY_WINDOWS,
  DeliveryWindowKey,
  parseShiftWindows,
  formatShiftWindows,
  toDateKey,
  getDayOfWeekName,
  formatCalendarDateLabel,
  getScheduleShiftForDate,
  isScheduleShiftMarkedOff,
} from '../context/AuthContext';
import QuizQuestionAdmin from './QuizQuestionAdmin';

const DAYS_OF_WEEK: DayOfWeek[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const DRIVER_COLOR_PALETTE = [
  { bg: '#1565C0', hover: '#0D47A1' }, // Blue
  { bg: '#2E7D32', hover: '#1B5E20' }, // Green
  { bg: '#6A1B9A', hover: '#4A148C' }, // Purple
  { bg: '#D84315', hover: '#BF360C' }, // Deep Orange
  { bg: '#00695C', hover: '#004D40' }, // Teal
  { bg: '#AD1457', hover: '#880E4F' }, // Pink / Magenta
  { bg: '#283593', hover: '#1A237E' }, // Indigo
  { bg: '#4E342E', hover: '#3E2723' }, // Brown
  { bg: '#0277BD', hover: '#01579B' }, // Light Blue
  { bg: '#558B2F', hover: '#33691E' }, // Olive Green
  { bg: '#C62828', hover: '#B71C1C' }, // Red
  { bg: '#4527A0', hover: '#311B92' }, // Deep Violet
  { bg: '#00838F', hover: '#006064' }, // Cyan
  { bg: '#EF6C00', hover: '#E65100' }, // Amber Orange
];

const getDriverColor = (driverId: string, allDrivers: SDSUser[]) => {
  const sortedIds = [...allDrivers].map((d) => d.id).sort();
  const idx = sortedIds.indexOf(driverId);
  if (idx >= 0) {
    // Use golden-angle stepping if more drivers than palette size so every new driver still gets a unique color
    if (idx < DRIVER_COLOR_PALETTE.length) {
      return DRIVER_COLOR_PALETTE[idx];
    }
    const hue = Math.round((idx * 137.508) % 360);
    return {
      bg: `hsl(${hue}, 68%, 36%)`,
      hover: `hsl(${hue}, 68%, 28%)`,
    };
  }
  let hash = 0;
  for (let i = 0; i < driverId.length; i++) {
    hash = driverId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return DRIVER_COLOR_PALETTE[Math.abs(hash) % DRIVER_COLOR_PALETTE.length];
};

export const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export default function AdminDashboard() {
  const {
    currentUser,
    users,
    schedules,
    scheduleLogs,
    addDriverAccount,
    removeDriverAccount,
    resetPassword,
    updateUserRole,
    updateUserDetails,
    requireKnowledgeQuizRetake,
    updateDriverSchedule,
    clearAllDriverSchedules,
  } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const [sectionMenuAnchor, setSectionMenuAnchor] = useState<null | HTMLElement>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Real Calendar & Drag-and-drop schedule state
  const [calendarViewMode, setCalendarViewMode] = useState<'week' | 'month'>('month');
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date());
  const [selectedDriverFilterIds, setSelectedDriverFilterIds] = useState<string[]>([]);
  const [draggingDriverId, setDraggingDriverId] = useState<string | null>(null);
  const [activeDropZone, setActiveDropZone] = useState<string | null>(null);
  const [scheduleSuccess, setScheduleSuccess] = useState<string | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [clearScheduleOpen, setClearScheduleOpen] = useState(false);
  const [clearingSchedule, setClearingSchedule] = useState(false);
  const [shiftMenuAnchor, setShiftMenuAnchor] = useState<{
    el: HTMLElement;
    driver: SDSUser;
    date: Date;
    windowKey: DeliveryWindowKey;
  } | null>(null);
  const [addToShiftMenuAnchor, setAddToShiftMenuAnchor] = useState<{
    el: HTMLElement;
    date: Date;
    windowKey: DeliveryWindowKey;
  } | null>(null);

  // Monthly Bulk Assignment Modal state
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkDriverId, setBulkDriverId] = useState<string>('');
  const [bulkMonthValue, setBulkMonthValue] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [bulkWindows, setBulkWindows] = useState<Record<DeliveryWindowKey, boolean>>({
    morning: true,
    afternoon: false,
    evening: false,
  });
  const [bulkDays, setBulkDays] = useState<Record<DayOfWeek, boolean>>({
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: false,
    Sunday: false,
  });
  const [bulkActionMode, setBulkActionMode] = useState<'assign' | 'remove'>('assign');
  const [savingBulk, setSavingBulk] = useState(false);

  // Add user modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<UserRole>('driver');
  const [newStatus, setNewStatus] = useState<DriverStatus>('Available');
  const [creatingUser, setCreatingUser] = useState(false);
  const [retakeTarget, setRetakeTarget] = useState<SDSUser | null>(null);
  const [retakeSaving, setRetakeSaving] = useState(false);
  const [accountMsg, setAccountMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );

  // Edit User (Name, Email, Phone, Role) modal state
  const [editingUser, setEditingUser] = useState<SDSUser | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editRole, setEditRole] = useState<UserRole>('driver');
  const [savingEdit, setSavingEdit] = useState(false);

  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';

  if (!currentUser || (!isAdmin && !isManager)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '220px',
          bgcolor: 'grey.50',
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <SecurityIcon color="error" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Admin & Manager Access Only
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You must be logged in with an SDS Administrator or Manager account to view this portal.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const roleOrder: Record<UserRole, number> = {
    admin: 0,
    manager: 1,
    driver: 2,
  };

  const drivers = [...users].sort((a, b) => {
    const roleDiff = roleOrder[a.role] - roleOrder[b.role];
    if (roleDiff !== 0) return roleDiff;
    return a.name.localeCompare(b.name);
  });

  const DEFAULT_EMPTY_SHIFTS: Record<DayOfWeek, string> = {
    Monday: 'Off',
    Tuesday: 'Off',
    Wednesday: 'Off',
    Thursday: 'Off',
    Friday: 'Off',
    Saturday: 'Off',
    Sunday: 'Off',
  };

  // Generate visible calendar dates for either the current week (Sun-Sat) or the selected month
  const getVisibleCalendarDates = (): Date[] => {
    if (calendarViewMode === 'week') {
      const d = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate());
      const sunday = new Date(d);
      sunday.setDate(d.getDate() - d.getDay());
      return Array.from({ length: 7 }, (_, i) => {
        const dt = new Date(sunday);
        dt.setDate(sunday.getDate() + i);
        return dt;
      });
    } else {
      const year = anchorDate.getFullYear();
      const month = anchorDate.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
    }
  };

  const visibleDates = getVisibleCalendarDates();

  const handlePrevPeriod = () => {
    setAnchorDate((prev) => {
      if (calendarViewMode === 'week') {
        const next = new Date(prev);
        next.setDate(prev.getDate() - 7);
        return next;
      }
      return new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
    });
  };

  const handleNextPeriod = () => {
    setAnchorDate((prev) => {
      if (calendarViewMode === 'week') {
        const next = new Date(prev);
        next.setDate(prev.getDate() + 7);
        return next;
      }
      return new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
    });
  };

  const handleJumpToToday = () => {
    setAnchorDate(new Date());
    setTimeout(() => {
      const todayEl = document.getElementById(`calendar-day-${toDateKey(new Date())}`);
      if (todayEl) {
        todayEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 80);
  };

  const toggleDriverFilter = (driverId: string) => {
    setSelectedDriverFilterIds((prev) =>
      prev.includes(driverId) ? prev.filter((id) => id !== driverId) : [...prev, driverId]
    );
  };

  const getPeriodHeaderLabel = (): string => {
    if (calendarViewMode === 'month') {
      return anchorDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (visibleDates.length === 0) return '';
    const first = visibleDates[0];
    const last = visibleDates[visibleDates.length - 1];
    const firstStr = first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const lastStr = last.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${firstStr} – ${lastStr}`;
  };

  const assignDriverToDateWindow = async (
    driverId: string,
    date: Date,
    windowKey: DeliveryWindowKey
  ) => {
    if (!isAdmin) return;
    const driver = drivers.find((d) => d.id === driverId);
    if (!driver) return;

    const existingSched = schedules.find((s) => s.driverId === driver.id);
    const currentShifts: Record<DayOfWeek, string> = {
      ...DEFAULT_EMPTY_SHIFTS,
      ...(existingSched?.shifts || {}),
    };
    const currentDateShifts: Record<string, string> = {
      ...(existingSched?.dateShifts || {}),
    };

    const dateKey = toDateKey(date);
    const currentShiftStr = getScheduleShiftForDate(existingSched, date);
    const existingWindows = parseShiftWindows(currentShiftStr);
    if (existingWindows.includes(windowKey)) {
      return;
    }

    const updatedWindows = [...existingWindows, windowKey];
    currentDateShifts[dateKey] = formatShiftWindows(updatedWindows);

    const windowObj = DELIVERY_WINDOWS.find((w) => w.key === windowKey);
    const windowLabel = windowObj ? `${windowObj.label} (${windowObj.timeRange})` : windowKey;
    const dateLabel = formatCalendarDateLabel(date);

    setScheduleError(null);
    const res = await updateDriverSchedule(
      driver.id,
      driver.name,
      currentShifts,
      existingSched?.markedOff,
      {
        day: dateLabel,
        windowLabel,
        action: 'Assigned',
        details: `Assigned ${driver.name} to ${dateLabel} — ${windowLabel}`,
      },
      currentDateShifts
    );
    if (!res.success) {
      setScheduleError(res.error || 'Failed to save schedule to Firebase.');
      return;
    }
    setScheduleSuccess(
      `Saved to Firebase: ${driver.name} assigned to ${dateLabel} — ${windowObj?.label} (${windowObj?.timeRange}).`
    );
    setTimeout(() => setScheduleSuccess(null), 3500);
  };

  const removeDriverFromDateWindow = async (
    driver: SDSUser,
    date: Date,
    windowKey: DeliveryWindowKey
  ) => {
    if (!isAdmin) return;
    const existingSched = schedules.find((s) => s.driverId === driver.id);
    const currentShifts: Record<DayOfWeek, string> = {
      ...DEFAULT_EMPTY_SHIFTS,
      ...(existingSched?.shifts || {}),
    };
    const currentDateShifts: Record<string, string> = {
      ...(existingSched?.dateShifts || {}),
    };

    const dateKey = toDateKey(date);
    const currentShiftStr = getScheduleShiftForDate(existingSched, date);
    const existingWindows = parseShiftWindows(currentShiftStr);
    const updatedWindows = existingWindows.filter((k) => k !== windowKey);
    currentDateShifts[dateKey] = formatShiftWindows(updatedWindows);

    const currentMarkedOff: Record<string, boolean> = {
      ...(existingSched?.markedOff || {}),
    };
    delete currentMarkedOff[`${dateKey}-${windowKey}`];

    const windowObj = DELIVERY_WINDOWS.find((w) => w.key === windowKey);
    const windowLabel = windowObj ? `${windowObj.label} (${windowObj.timeRange})` : windowKey;
    const dateLabel = formatCalendarDateLabel(date);

    setScheduleError(null);
    const res = await updateDriverSchedule(
      driver.id,
      driver.name,
      currentShifts,
      currentMarkedOff,
      {
        day: dateLabel,
        windowLabel,
        action: 'Removed',
        details: `Removed ${driver.name} from ${dateLabel} — ${windowLabel}`,
      },
      currentDateShifts
    );
    if (!res.success) {
      setScheduleError(res.error || 'Failed to update schedule in Firebase.');
    }
  };

  const toggleDriverMarkedOffForDateShift = async (
    driver: SDSUser,
    date: Date,
    windowKey: DeliveryWindowKey
  ) => {
    if (!isAdmin) return;
    const existingSched = schedules.find((s) => s.driverId === driver.id);
    const currentShifts: Record<DayOfWeek, string> = {
      ...DEFAULT_EMPTY_SHIFTS,
      ...(existingSched?.shifts || {}),
    };
    const currentDateShifts: Record<string, string> = {
      ...(existingSched?.dateShifts || {}),
    };
    const currentMarkedOff: Record<string, boolean> = {
      ...(existingSched?.markedOff || {}),
    };

    const dateKey = toDateKey(date);
    const shiftKey = `${dateKey}-${windowKey}`;
    const currentlyOff = isScheduleShiftMarkedOff(existingSched, date, windowKey);
    const newOffState = !currentlyOff;
    currentMarkedOff[shiftKey] = newOffState;

    const windowObj = DELIVERY_WINDOWS.find((w) => w.key === windowKey);
    const windowLabel = windowObj ? `${windowObj.label} (${windowObj.timeRange})` : windowKey;
    const dateLabel = formatCalendarDateLabel(date);

    setScheduleError(null);
    const res = await updateDriverSchedule(
      driver.id,
      driver.name,
      currentShifts,
      currentMarkedOff,
      {
        day: dateLabel,
        windowLabel,
        action: newOffState ? 'Marked Off' : 'Restored Active',
        details: newOffState
          ? `Marked ${driver.name} as (off) for ${dateLabel} — ${windowLabel}`
          : `Removed (off) status for ${driver.name} on ${dateLabel} — ${windowLabel}`,
      },
      currentDateShifts
    );
    if (!res.success) {
      setScheduleError(res.error || 'Failed to update driver shift-off status in Firebase.');
      return;
    }
    const winLabel = windowObj?.label || windowKey;
    setScheduleSuccess(
      newOffState
        ? `Marked ${driver.name} as (off) for ${dateLabel} — ${winLabel}.`
        : `Restored ${driver.name} as active for ${dateLabel} — ${winLabel}.`
    );
    setTimeout(() => setScheduleSuccess(null), 3500);
  };

  // Assign or remove a driver for every matching weekday across the entire month directly from a single shift menu or from the Bulk Modal
  const applyMonthlyAssignmentForWeekdays = async (params: {
    driver: SDSUser;
    year: number;
    monthIndex: number; // 0-11
    targetDays: DayOfWeek[];
    targetWindows: DeliveryWindowKey[];
    mode: 'assign' | 'remove';
  }) => {
    if (!isAdmin) return;
    const { driver, year, monthIndex, targetDays, targetWindows, mode } = params;
    const existingSched = schedules.find((s) => s.driverId === driver.id);
    const currentShifts: Record<DayOfWeek, string> = {
      ...DEFAULT_EMPTY_SHIFTS,
      ...(existingSched?.shifts || {}),
    };
    const currentDateShifts: Record<string, string> = {
      ...(existingSched?.dateShifts || {}),
    };
    const currentMarkedOff: Record<string, boolean> = {
      ...(existingSched?.markedOff || {}),
    };

    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    let affectedDatesCount = 0;

    for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
      const dt = new Date(year, monthIndex, dayNum);
      const dow = getDayOfWeekName(dt);
      if (!targetDays.includes(dow)) continue;

      affectedDatesCount++;
      const dKey = toDateKey(dt);
      const existingShiftStr =
        currentDateShifts[dKey] !== undefined
          ? currentDateShifts[dKey]
          : currentShifts[dow] || 'Off';
      const existingWinKeys = parseShiftWindows(existingShiftStr);

      let nextWinKeys: DeliveryWindowKey[];
      if (mode === 'assign') {
        nextWinKeys = Array.from(new Set([...existingWinKeys, ...targetWindows]));
      } else {
        nextWinKeys = existingWinKeys.filter((k) => !targetWindows.includes(k));
        targetWindows.forEach((wk) => {
          delete currentMarkedOff[`${dKey}-${wk}`];
        });
      }
      currentDateShifts[dKey] = formatShiftWindows(nextWinKeys);
    }

    const monthName = new Date(year, monthIndex, 1).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
    const windowsText = targetWindows
      .map((wk) => DELIVERY_WINDOWS.find((w) => w.key === wk)?.label || wk)
      .join(', ');
    const daysText = targetDays.map((d) => d.slice(0, 3)).join(', ');

    setScheduleError(null);
    const res = await updateDriverSchedule(
      driver.id,
      driver.name,
      currentShifts,
      currentMarkedOff,
      {
        day: `${monthName} (${daysText})`,
        windowLabel: windowsText,
        action: mode === 'assign' ? 'Bulk Assigned' : 'Removed',
        details:
          mode === 'assign'
            ? `Assigned ${driver.name} to ${windowsText} for all ${daysText} in ${monthName} (${affectedDatesCount} days)`
            : `Removed ${driver.name} from ${windowsText} for all ${daysText} in ${monthName} (${affectedDatesCount} days)`,
      },
      currentDateShifts
    );

    if (!res.success) {
      setScheduleError(res.error || 'Failed to save monthly schedule to Firebase.');
      return;
    }

    setScheduleSuccess(
      mode === 'assign'
        ? `Saved to Firebase: Assigned ${driver.name} to ${windowsText} across ${affectedDatesCount} days in ${monthName}.`
        : `Saved to Firebase: Removed ${driver.name} from ${windowsText} across ${affectedDatesCount} days in ${monthName}.`
    );
    setTimeout(() => setScheduleSuccess(null), 4500);
  };

  const handleBulkModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;
    const driver = drivers.find((d) => d.id === bulkDriverId);
    if (!driver) {
      setScheduleError('Please select a driver.');
      return;
    }
    const selectedWindows = (Object.keys(bulkWindows) as DeliveryWindowKey[]).filter(
      (k) => bulkWindows[k]
    );
    if (selectedWindows.length === 0) {
      setScheduleError('Please select at least one shift window (Morning, Afternoon, or Evening).');
      return;
    }
    const selectedDays = DAYS_OF_WEEK.filter((d) => bulkDays[d]);
    if (selectedDays.length === 0) {
      setScheduleError('Please select at least one day of the week.');
      return;
    }

    const [yearStr, monthStr] = bulkMonthValue.split('-');
    const year = parseInt(yearStr, 10);
    const monthIndex = parseInt(monthStr, 10) - 1;

    setSavingBulk(true);
    await applyMonthlyAssignmentForWeekdays({
      driver,
      year,
      monthIndex,
      targetDays: selectedDays,
      targetWindows: selectedWindows,
      mode: bulkActionMode,
    });
    setSavingBulk(false);
    setBulkModalOpen(false);
    // Jump calendar to the month that was just updated
    setAnchorDate(new Date(year, monthIndex, 1));
  };

  const getWindowIcon = (key: DeliveryWindowKey) => {
    if (key === 'morning') return <WbSunnyOutlinedIcon sx={{ fontSize: 16, color: '#f57c00' }} />;
    if (key === 'afternoon') return <LightModeOutlinedIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
    return <NightsStayOutlinedIcon sx={{ fontSize: 16, color: '#5e35b1' }} />;
  };

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;
    setAccountMsg(null);
    if (!newName.trim() || !newEmail.trim() || !newPassword.trim()) {
      setAccountMsg({ type: 'error', text: 'Name, Email, and Password are required.' });
      return;
    }
    if (newPassword.length < 6) {
      setAccountMsg({
        type: 'error',
        text: 'Password must be at least 6 characters for Firebase Authentication.',
      });
      return;
    }

    setCreatingUser(true);
    const res = await addDriverAccount({
      name: newName,
      email: newEmail,
      phone: newPhone,
      password: newPassword,
      role: newRole,
      status: newStatus,
    });
    setCreatingUser(false);

    if (!res.success) {
      setAccountMsg({ type: 'error', text: res.error || 'Could not create user login.' });
    } else {
      setAccountMsg({
        type: 'success',
        text: `Successfully created ${newRole.toUpperCase()} account for ${newName} (${newEmail}).`,
      });
      setNewName('');
      setNewEmail('');
      setNewPhone('');
      setNewPassword('');
      setNewRole('driver');
      setAddModalOpen(false);
    }
  };

  const openUserEditor = (user: SDSUser) => {
    if (!isAdmin) return;
    setEditingUser(user);
    setEditName(user.name);
    setEditEmail(user.email);
    setEditPhone(formatPhoneNumber(user.phone || ''));
    setEditRole(user.role);
  };

  const handleSaveUserEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser || !isAdmin) return;
    setSavingEdit(true);
    const res = await updateUserDetails(editingUser.id, {
      name: editName,
      email: editEmail,
      phone: editPhone,
      role: editRole,
    });
    setSavingEdit(false);

    if (!res.success) {
      setAccountMsg({ type: 'error', text: res.error || 'Failed to update user details.' });
    } else {
      setAccountMsg({
        type: 'success',
        text: `Updated profile for ${editName.trim()} (${editEmail.trim()}).`,
      });
      setEditingUser(null);
    }
  };

  const getRoleColor = (role: UserRole): 'secondary' | 'primary' | 'success' => {
    if (role === 'admin') return 'secondary'; // Purple
    if (role === 'manager') return 'primary'; // Blue
    return 'success'; // Green for Driver
  };

  const formatRosterLogin = (value?: string) => {
    if (!value) return { date: 'Never', time: '' };
    const parsed = new Date(value);
    return {
      date: parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
      time: parsed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
    };
  };

  const sendPasswordReset = async (user: SDSUser) => {
    const res = await resetPassword(user.email);
    if (res.success) {
      setAccountMsg({
        type: 'success',
        text: `Password reset email sent to ${user.name} (${user.email}).`,
      });
    } else {
      setAccountMsg({
        type: 'error',
        text: res.error || 'Failed to send password reset email.',
      });
    }
  };

  const renderKnowledgeTest = (user: SDSUser, fullWidthAction = false) => (
    <>
      {user.role === 'driver' && user.knowledgeTrainingRequired ? (
        user.knowledgeQuizPassed ? (
          <Box>
            <Chip size="small" color="success" label="Passed Test" sx={{ fontWeight: 700 }} />
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {user.knowledgeQuizScore}%
            </Typography>
          </Box>
        ) : user.knowledgeQuizScore != null || (user.knowledgeQuizAttempts || 0) > 0 ? (
          <Box>
            <Chip size="small" color="warning" label="Retake required" />
            {user.knowledgeQuizScore != null && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {user.knowledgeQuizScore}%
              </Typography>
            )}
          </Box>
        ) : (
          <Chip size="small" variant="outlined" label="Not taken" />
        )
      ) : fullWidthAction ? null : (
        '—'
      )}
      {isAdmin && user.role === 'driver' && !(user.knowledgeTrainingRequired && !user.knowledgeQuizPassed) && (
        <Button
          size="small"
          variant="outlined"
          color="warning"
          onClick={() => setRetakeTarget(user)}
          sx={{ mt: 1, display: 'flex', whiteSpace: 'nowrap', width: fullWidthAction ? '100%' : 'auto' }}
        >
          {user.knowledgeQuizPassed || user.knowledgeQuizScore != null ? 'Require retake' : 'Require quiz'}
        </Button>
      )}
    </>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        paddingTop: { xs: '84px', sm: '260px', md: '230px' },
        pb: 8,
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 5 } }}>
        {/* Header Summary */}
        <Box sx={{ mb: 3, textAlign: { xs: 'center', md: 'left' } }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
              lineHeight: 1.2,
              background: 'linear-gradient(45deg, #0D47A1 30%, #1976d2 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {isManager ? 'SDS Manager Portal' : 'SDS Admin Portal'}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.75, mx: { xs: 'auto', md: 0 }, maxWidth: { xs: 360, md: 560 } }}
          >
            {isManager
              ? 'View drivers and schedules. Only administrators can make changes.'
              : 'Manage drivers, schedules, quiz questions, and schedule logs.'}
          </Typography>
        </Box>

        {accountMsg && (
          <Alert
            severity={accountMsg.type}
            onClose={() => setAccountMsg(null)}
            sx={{ mb: 2.5, borderRadius: 2 }}
          >
            {accountMsg.text}
          </Alert>
        )}

        {/* Navigation Tabs */}
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {/* Mobile: one control, so more sections do not crowd the screen */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.25, bgcolor: 'primary.main' }}>
            {(() => {
              const sections = [
                { id: 0, label: 'Drivers', detail: 'Roster and accounts', icon: <PeopleAltIcon fontSize="small" /> },
                { id: 1, label: 'Schedule', detail: 'Driver calendar', icon: <CalendarMonthIcon fontSize="small" /> },
                ...(isAdmin
                  ? [
                      { id: 2, label: 'Logs', detail: 'Schedule changes', icon: <FactCheckIcon fontSize="small" /> },
                      { id: 3, label: 'Quiz', detail: 'Questions and answers', icon: <QuizOutlinedIcon fontSize="small" /> },
                    ]
                  : []),
              ];
              const current = sections.find((item) => item.id === activeTab) || sections[0];
              return (
                <>
                  <Button
                    fullWidth
                    aria-haspopup="listbox"
                    aria-expanded={Boolean(sectionMenuAnchor)}
                    aria-label="Admin section"
                    onClick={(event) => setSectionMenuAnchor(event.currentTarget)}
                    startIcon={current.icon}
                    endIcon={<ArrowDropDownIcon />}
                    sx={{
                      justifyContent: 'flex-start',
                      bgcolor: 'white',
                      color: 'primary.main',
                      fontWeight: 800,
                      fontSize: '1rem',
                      py: 1.35,
                      px: 1.75,
                      borderRadius: 2,
                      '& .MuiButton-endIcon': { ml: 'auto' },
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                  >
                    {current.label}
                  </Button>
                  <Menu
                    anchorEl={sectionMenuAnchor}
                    open={Boolean(sectionMenuAnchor)}
                    onClose={() => setSectionMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                      sx: {
                        width: sectionMenuAnchor ? sectionMenuAnchor.clientWidth : undefined,
                        mt: 0.75,
                        borderRadius: 2,
                      },
                    }}
                  >
                    {sections.map((item) => (
                      <MenuItem
                        key={item.id}
                        selected={item.id === activeTab}
                        onClick={() => {
                          setActiveTab(item.id);
                          setSectionMenuAnchor(null);
                          window.scrollTo({ top: 0 });
                        }}
                        sx={{ py: 1.25 }}
                      >
                        <ListItemIcon sx={{ color: item.id === activeTab ? 'primary.main' : 'text.secondary' }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          secondary={item.detail}
                          primaryTypographyProps={{ fontWeight: 700 }}
                        />
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              );
            })()}
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              display: { xs: 'none', md: 'flex' },
              bgcolor: 'primary.main',
              color: 'white',
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.75)',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 60,
              },
              '& .Mui-selected': {
                color: '#ffffff !important',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#ffffff',
                height: 3,
              },
            }}
          >
            <Tab icon={<PeopleAltIcon />} iconPosition="start" label="Available Drivers & Users" />
            <Tab icon={<CalendarMonthIcon />} iconPosition="start" label="Driver Schedule" />
            {isAdmin && (
              <Tab icon={<FactCheckIcon />} iconPosition="start" label="Schedule Change Logs" />
            )}
            {isAdmin && <Tab icon={<QuizOutlinedIcon />} iconPosition="start" label="Quiz Questions" />}
          </Tabs>

          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            {/* TAB 0: AVAILABLE DRIVERS & USERS */}
            {activeTab === 0 && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Driver & Staff Roster
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
                      {isAdmin
                        ? 'View all SDS users, edit their name/email, change their role (Driver / Manager / Admin), or remove logins.'
                        : 'View all registered SDS drivers and staff members.'}
                    </Typography>
                  </Box>
                  {isAdmin && (
                    <Button
                      variant="contained"
                      startIcon={<PersonAddAlt1Icon />}
                      onClick={() => {
                        setAccountMsg(null);
                        setAddModalOpen(true);
                      }}
                      sx={{
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        width: { xs: '100%', sm: 'auto' },
                        px: 2.5,
                        py: 1,
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        borderRadius: 2,
                      }}
                    >
                      Add User / Driver
                    </Button>
                  )}
                </Box>

                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
                  {drivers.map((user) => {
                    const drvColor = getDriverColor(user.id, drivers);
                    const lastLogin = formatRosterLogin(user.lastLogin);
                    return (
                      <Paper
                        key={user.id}
                        variant="outlined"
                        sx={{ borderRadius: 2.5, overflow: 'hidden' }}
                      >
                        <Box sx={{ height: 6, bgcolor: drvColor.bg }} />
                        <Box sx={{ p: 1.75, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                fontWeight={800}
                                sx={{ color: drvColor.bg, fontSize: '1.05rem', lineHeight: 1.25, wordBreak: 'break-word' }}
                              >
                                {user.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.35, wordBreak: 'break-all' }}
                              >
                                {user.email}
                              </Typography>
                            </Box>
                            {!isAdmin && (
                              <Chip
                                size="small"
                                label={user.role.toUpperCase()}
                                color={getRoleColor(user.role)}
                                sx={{ fontWeight: 700, flexShrink: 0 }}
                              />
                            )}
                          </Box>

                          {isAdmin && (
                            <FormControl size="small" fullWidth>
                              <InputLabel id={`role-${user.id}`}>Role</InputLabel>
                              <Select
                                labelId={`role-${user.id}`}
                                label="Role"
                                value={user.role}
                                disabled={user.id === currentUser.id}
                                onChange={(e) => updateUserRole(user.id, e.target.value as UserRole)}
                              >
                                <MenuItem value="driver">Driver</MenuItem>
                                <MenuItem value="manager">Manager</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                              </Select>
                            </FormControl>
                          )}

                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: 1.25,
                              bgcolor: 'grey.50',
                              borderRadius: 2,
                              p: 1.25,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                Phone
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {user.phone ? formatPhoneNumber(user.phone) : '—'}
                              </Typography>
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                Last login
                              </Typography>
                              <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                                {lastLogin.date}
                              </Typography>
                              {lastLogin.time && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {lastLogin.time}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {user.role === 'driver' && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                                Knowledge test
                              </Typography>
                              {renderKnowledgeTest(user, true)}
                            </Box>
                          )}

                          {isAdmin && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                startIcon={<LockResetIcon />}
                                onClick={() => sendPasswordReset(user)}
                                sx={{ minWidth: 0, px: 0.75, fontSize: '0.75rem' }}
                              >
                                Reset
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditOutlinedIcon />}
                                onClick={() => openUserEditor(user)}
                                sx={{ minWidth: 0, px: 0.75, fontSize: '0.75rem' }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                disabled={user.id === currentUser.id}
                                onClick={() => removeDriverAccount(user.id)}
                                sx={{ minWidth: 0, px: 0.75, fontSize: '0.75rem' }}
                              >
                                Remove
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                  {users.length === 0 && (
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
                      <Typography color="text.secondary">No users found.</Typography>
                    </Paper>
                  )}
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, display: { xs: 'none', md: 'block' } }}>
                  <Table>
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Knowledge Test</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last Login</TableCell>
                        {isAdmin && (
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Edit / Remove
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drivers.map((user) => {
                        const drvColor = getDriverColor(user.id, drivers);
                        return (
                          <TableRow
                            key={user.id}
                            hover
                            sx={{
                              borderLeft: `5px solid ${drvColor.bg}`,
                            }}
                          >
                            <TableCell>
                              <Chip
                                label={user.name}
                                size="small"
                                sx={{
                                  bgcolor: drvColor.bg,
                                  color: '#ffffff',
                                  fontWeight: 700,
                                  fontSize: '0.8rem',
                                }}
                              />
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.phone ? formatPhoneNumber(user.phone) : '—'}</TableCell>
                            <TableCell>
                              {isAdmin ? (
                                <Select
                                  size="small"
                                  value={user.role}
                                  disabled={user.id === currentUser.id}
                                  onChange={(e) =>
                                    updateUserRole(user.id, e.target.value as UserRole)
                                  }
                                  sx={{ minWidth: 120 }}
                                >
                                  <MenuItem value="driver">Driver</MenuItem>
                                  <MenuItem value="manager">Manager</MenuItem>
                                  <MenuItem value="admin">Admin</MenuItem>
                                </Select>
                              ) : (
                                <Chip
                                  size="small"
                                  label={user.role.toUpperCase()}
                                  color={getRoleColor(user.role)}
                                />
                              )}
                            </TableCell>
                            <TableCell>{renderKnowledgeTest(user)}</TableCell>
                            <TableCell>
                              {user.lastLogin
                                ? new Date(user.lastLogin).toLocaleString()
                                : 'Never'}
                            </TableCell>
                            {isAdmin && (
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                <IconButton
                                  color="info"
                                  size="small"
                                  title="Send password reset email"
                                  onClick={() => sendPasswordReset(user)}
                                  sx={{ mr: 0.5 }}
                                >
                                  <LockResetIcon />
                                </IconButton>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  title="Edit user name, email & phone"
                                  onClick={() => openUserEditor(user)}
                                  sx={{ mr: 0.5 }}
                                >
                                  <EditOutlinedIcon />
                                </IconButton>
                                <IconButton
                                  color="error"
                                  size="small"
                                  disabled={user.id === currentUser.id}
                                  title={
                                    user.id === currentUser.id
                                      ? 'Cannot remove your own active admin account'
                                      : 'Remove user login'
                                  }
                                  onClick={() => removeDriverAccount(user.id)}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      {users.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={isAdmin ? 7 : 6} align="center" sx={{ py: 4 }}>
                            No users found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Dialog open={Boolean(retakeTarget)} onClose={() => !retakeSaving && setRetakeTarget(null)}>
                  <DialogTitle>Require quiz retake</DialogTitle>
                  <DialogContent>
                    <Typography variant="body1">
                      {retakeTarget
                        ? `${retakeTarget.name} will need to pass the knowledge quiz again before opening My Schedule. Topics already marked as read stay marked.`
                        : ''}
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setRetakeTarget(null)} disabled={retakeSaving}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      disabled={retakeSaving || !retakeTarget}
                      onClick={async () => {
                        if (!retakeTarget) return;
                        setRetakeSaving(true);
                        const res = await requireKnowledgeQuizRetake(retakeTarget.id);
                        setRetakeSaving(false);
                        if (res.success) {
                          setAccountMsg({
                            type: 'success',
                            text: `${retakeTarget.name} must pass the knowledge quiz again.`,
                          });
                          setRetakeTarget(null);
                        } else {
                          setAccountMsg({
                            type: 'error',
                            text: res.error || 'Could not require a quiz retake.',
                          });
                        }
                      }}
                    >
                      {retakeSaving ? 'Saving...' : 'Require retake'}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}

            {/* TAB 1: DRIVER SCHEDULE */}
            {activeTab === 1 && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', md: 'center' },
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' }, lineHeight: 1.3 }}
                    >
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {isAdmin
                          ? 'Driver Delivery Calendar (Drag & Drop + Monthly Scheduling)'
                          : 'Driver Delivery Calendar (View Only)'}
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Driver Delivery Calendar
                      </Box>
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: { xs: 'none', sm: 'block' } }}
                    >
                      {isAdmin ? (
                        <>
                          Drag any driver onto a specific calendar date's shift window, or use{' '}
                          <strong>Assign Driver for Month</strong> to schedule a driver across an entire month. Click any scheduled driver's dropdown arrow to mark them <strong>(off)</strong>, assign for the whole month, or remove them.
                        </>
                      ) : (
                        'Managers can view the full calendar schedule for all drivers across all delivery windows. Modifying the schedule requires Administrator privileges.'
                      )}
                    </Typography>
                  </Box>

                  {isAdmin && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        width: { xs: '100%', md: 'auto' },
                        flexShrink: 0,
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<DateRangeIcon />}
                        onClick={() => {
                          if (!bulkDriverId && drivers.length > 0) {
                            setBulkDriverId(drivers[0].id);
                          }
                          setBulkMonthValue(
                            `${anchorDate.getFullYear()}-${String(
                              anchorDate.getMonth() + 1
                            ).padStart(2, '0')}`
                          );
                          setBulkModalOpen(true);
                        }}
                        sx={{
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          width: { xs: '100%', sm: 'auto' },
                          px: 2.5,
                          py: 1,
                          fontSize: { xs: '0.85rem', sm: '0.95rem' },
                          fontWeight: 700,
                          borderRadius: 2,
                          background: 'linear-gradient(90deg, #0D47A1 0%, #1976d2 100%)',
                        }}
                      >
                        Assign Driver for Month
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => setClearScheduleOpen(true)}
                        sx={{
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          width: { xs: '100%', sm: 'auto' },
                          px: 2.5,
                          py: 1,
                          fontSize: { xs: '0.85rem', sm: '0.95rem' },
                          fontWeight: 700,
                          borderRadius: 2,
                        }}
                      >
                        Clear All Schedules
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* Calendar Navigation Toolbar */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1.25, sm: 1.5 },
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                  }}
                >
                  {/* Mobile toolbar */}
                  <Box
                    sx={{
                      display: { xs: 'flex', sm: 'none' },
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: 1.25,
                    }}
                  >
                    <Box sx={{ position: 'relative', textAlign: 'center', px: 1 }}>
                      <Typography
                        fontWeight={800}
                        color="primary.dark"
                        sx={{
                          fontSize: '1.15rem',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.6,
                        }}
                      >
                        {getPeriodHeaderLabel()}
                        <CalendarMonthIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      </Typography>
                      <Box
                        component="input"
                        type="month"
                        aria-label="Jump to month"
                        value={`${anchorDate.getFullYear()}-${String(
                          anchorDate.getMonth() + 1
                        ).padStart(2, '0')}`}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (!e.target.value) return;
                          const [y, m] = e.target.value.split('-');
                          setAnchorDate(new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1));
                        }}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          opacity: 0,
                          cursor: 'pointer',
                          width: '100%',
                          height: '100%',
                          border: 0,
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={handlePrevPeriod}
                        title={calendarViewMode === 'month' ? 'Previous Month' : 'Previous Week'}
                        sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<TodayIcon />}
                        onClick={handleJumpToToday}
                        sx={{ bgcolor: 'white', fontWeight: 700, px: 2, minWidth: 110 }}
                      >
                        Today
                      </Button>
                      <IconButton
                        size="small"
                        onClick={handleNextPeriod}
                        title={calendarViewMode === 'month' ? 'Next Month' : 'Next Week'}
                        sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>

                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      fullWidth
                      value={calendarViewMode}
                      onChange={(_, val) => {
                        if (val) setCalendarViewMode(val);
                      }}
                      sx={{ bgcolor: 'white' }}
                    >
                      <ToggleButton value="month" sx={{ fontWeight: 700, py: 0.85 }}>
                        Month
                      </ToggleButton>
                      <ToggleButton value="week" sx={{ fontWeight: 700, py: 0.85 }}>
                        Week
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {/* Desktop / tablet toolbar */}
                  <Box
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={handlePrevPeriod}
                        title={calendarViewMode === 'month' ? 'Previous Month' : 'Previous Week'}
                        sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<TodayIcon />}
                        onClick={handleJumpToToday}
                        sx={{ bgcolor: 'white', fontWeight: 600 }}
                      >
                        Today
                      </Button>
                      <IconButton
                        size="small"
                        onClick={handleNextPeriod}
                        title={calendarViewMode === 'month' ? 'Next Month' : 'Next Week'}
                        sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                      <Typography
                        variant="subtitle1"
                        fontWeight={800}
                        color="primary.dark"
                        sx={{ ml: 1, whiteSpace: 'nowrap' }}
                      >
                        {getPeriodHeaderLabel()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                      <TextField
                        type="month"
                        size="small"
                        label="Jump to Month"
                        value={`${anchorDate.getFullYear()}-${String(
                          anchorDate.getMonth() + 1
                        ).padStart(2, '0')}`}
                        onChange={(e) => {
                          if (!e.target.value) return;
                          const [y, m] = e.target.value.split('-');
                          setAnchorDate(new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1));
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ bgcolor: 'white', width: 175 }}
                      />

                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={calendarViewMode}
                        onChange={(_, val) => {
                          if (val) setCalendarViewMode(val);
                        }}
                        sx={{ bgcolor: 'white' }}
                      >
                        <ToggleButton value="month" sx={{ fontWeight: 700, px: 1.5 }}>
                          Full Month (
                          {new Date(
                            anchorDate.getFullYear(),
                            anchorDate.getMonth() + 1,
                            0
                          ).getDate()}{' '}
                          Days)
                        </ToggleButton>
                        <ToggleButton value="week" sx={{ fontWeight: 700, px: 1.5 }}>
                          7-Day Week
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Box>
                </Paper>

                {scheduleSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {scheduleSuccess}
                  </Alert>
                )}

                {scheduleError && (
                  <Alert severity="error" onClose={() => setScheduleError(null)} sx={{ mb: 2 }}>
                    {scheduleError}
                  </Alert>
                )}

                <Grid container spacing={2.5} alignItems="flex-start">
                  {/* LEFT SIDEBAR: DRAGGABLE DRIVERS LIST */}
                  <Grid item xs={12} md={3} lg={2.5} xl={2}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: { xs: 1.5, md: 2 },
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        position: { md: 'sticky' },
                        top: { md: 220 },
                      }}
                    >
                      {/* Mobile: compact checkbox dropdown */}
                      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="mobile-driver-filter-label" shrink>
                            Filter Drivers
                          </InputLabel>
                          <Select
                            labelId="mobile-driver-filter-label"
                            multiple
                            displayEmpty
                            notched
                            value={selectedDriverFilterIds}
                            label="Filter Drivers"
                            onChange={(e) => {
                              const next =
                                typeof e.target.value === 'string'
                                  ? e.target.value.split(',')
                                  : e.target.value;
                              if (next.includes('__all__')) {
                                setSelectedDriverFilterIds([]);
                                return;
                              }
                              setSelectedDriverFilterIds(next);
                            }}
                            renderValue={(selected) => {
                              if (selected.length === 0) return 'All Drivers';
                              if (selected.length === 1) {
                                return (
                                  drivers.find((d) => d.id === selected[0])?.name || '1 driver'
                                );
                              }
                              return `${selected.length} drivers selected`;
                            }}
                            input={<OutlinedInput notched label="Filter Drivers" />}
                            MenuProps={{
                              disablePortal: true,
                              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                              transformOrigin: { vertical: 'top', horizontal: 'left' },
                              PaperProps: {
                                sx: { maxHeight: 360, mt: 0.5, width: '100%' },
                              },
                            }}
                            sx={{ bgcolor: 'white', fontWeight: 600 }}
                          >
                            <MenuItem value="__all__">
                              <Checkbox
                                size="small"
                                checked={selectedDriverFilterIds.length === 0}
                                tabIndex={-1}
                                disableRipple
                              />
                              <ListItemText
                                primary="All Drivers"
                                primaryTypographyProps={{ fontWeight: 700 }}
                              />
                            </MenuItem>
                            {drivers.map((driver) => {
                              const drvColor = getDriverColor(driver.id, drivers);
                              const checked = selectedDriverFilterIds.includes(driver.id);
                              return (
                                <MenuItem key={driver.id} value={driver.id} sx={{ py: 0.75 }}>
                                  <Checkbox
                                    size="small"
                                    checked={checked}
                                    tabIndex={-1}
                                    disableRipple
                                  />
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      bgcolor: drvColor.bg,
                                      flexShrink: 0,
                                      mr: 1,
                                    }}
                                  />
                                  <ListItemText
                                    primary={driver.name}
                                    primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                                  />
                                  <Chip
                                    size="small"
                                    label={driver.role.toUpperCase()}
                                    color={getRoleColor(driver.role)}
                                    sx={{
                                      height: 18,
                                      fontSize: '0.58rem',
                                      fontWeight: 700,
                                      ml: 1,
                                      '& .MuiChip-label': { px: 0.6 },
                                    }}
                                  />
                                </MenuItem>
                              );
                            })}
                            {drivers.length === 0 && (
                              <MenuItem disabled>
                                <ListItemText primary="No drivers available" />
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Desktop: draggable driver cards */}
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <PeopleAltIcon color="primary" fontSize="small" /> Drivers ({drivers.length})
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          {isAdmin
                            ? 'Click a driver to filter schedule, or drag onto any window on the right'
                            : 'Click any driver card below to filter the schedule'}
                        </Typography>

                        {selectedDriverFilterIds.length > 0 && (
                          <Button
                            fullWidth
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={() => setSelectedDriverFilterIds([])}
                            sx={{ mb: 1.5, bgcolor: 'white', fontWeight: 600 }}
                          >
                            Show All Drivers ({selectedDriverFilterIds.length} selected)
                          </Button>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {drivers.map((driver) => {
                            const isDragging = draggingDriverId === driver.id;
                            const isFilteredSelected = selectedDriverFilterIds.includes(driver.id);
                            const isDimmedByFilter =
                              selectedDriverFilterIds.length > 0 && !isFilteredSelected;
                            const drvColor = getDriverColor(driver.id, drivers);
                            return (
                              <Paper
                                key={driver.id}
                                draggable={isAdmin}
                                onClick={() => toggleDriverFilter(driver.id)}
                                onDragStart={(e) => {
                                  if (!isAdmin) return;
                                  e.dataTransfer.setData('text/plain', driver.id);
                                  e.dataTransfer.effectAllowed = 'copy';
                                  setDraggingDriverId(driver.id);
                                }}
                                onDragEnd={() => {
                                  setDraggingDriverId(null);
                                  setActiveDropZone(null);
                                }}
                                elevation={isDragging || isFilteredSelected ? 4 : 1}
                                sx={{
                                  p: 1.25,
                                  borderRadius: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  cursor: isAdmin ? 'grab' : 'pointer',
                                  userSelect: 'none',
                                  border: '1px solid',
                                  borderColor:
                                    isDragging || isFilteredSelected ? drvColor.bg : 'divider',
                                  borderLeft: `5px solid ${drvColor.bg}`,
                                  bgcolor: isFilteredSelected
                                    ? 'rgba(25, 118, 210, 0.1)'
                                    : isDragging
                                    ? 'rgba(25, 118, 210, 0.08)'
                                    : 'white',
                                  opacity: isDimmedByFilter ? 0.5 : 1,
                                  transition: 'all 0.15s ease',
                                  '&:hover': {
                                    borderColor: drvColor.bg,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                                    transform: 'translateY(-1px)',
                                    opacity: 1,
                                  },
                                  ...(isAdmin
                                    ? {
                                        '&:active': {
                                          cursor: 'grabbing',
                                        },
                                      }
                                    : {}),
                                }}
                              >
                                {isAdmin && <DragIndicatorIcon fontSize="small" color="action" />}
                                <Box sx={{ overflow: 'hidden', flexGrow: 1, minWidth: 0 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography variant="body2" fontWeight={700} noWrap>
                                      {driver.name}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={driver.role.toUpperCase()}
                                      color={getRoleColor(driver.role)}
                                      sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }}
                                    />
                                  </Box>
                                </Box>
                              </Paper>
                            );
                          })}

                          {drivers.length === 0 && (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                              No drivers available. Add drivers in the "Available Drivers & Users" tab.
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* RIGHT AREA: REAL CALENDAR DATES WITH 3 DELIVERY WINDOWS EACH */}
                  <Grid item xs={12} md={9} lg={9.5} xl={10}>
                    <Grid container spacing={2}>
                      {visibleDates.map((dateObj) => {
                        const dateKey = toDateKey(dateObj);
                        const todayKey = toDateKey(new Date());
                        const isToday = dateKey === todayKey;
                        const fullDateHeader = formatCalendarDateLabel(dateObj);

                        return (
                          <Grid item xs={12} key={dateKey} id={`calendar-day-${dateKey}`}>
                            <Paper
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                borderColor: isToday ? '#0D47A1' : 'divider',
                                borderWidth: isToday ? 2 : 1,
                              }}
                            >
                              <Box
                                sx={{
                                  bgcolor: isToday ? '#0D47A1' : 'primary.main',
                                  color: 'white',
                                  px: { xs: 1.5, sm: 2 },
                                  py: { xs: 0.85, sm: 1 },
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={700}
                                  sx={{ fontSize: { xs: '0.92rem', sm: '1rem' }, whiteSpace: 'nowrap' }}
                                >
                                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                    {fullDateHeader}
                                  </Box>
                                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                    {dateObj.toLocaleDateString('en-US', {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                  </Box>
                                </Typography>
                                {isToday && (
                                  <Chip
                                    size="small"
                                    label="TODAY"
                                    sx={{
                                      bgcolor: 'rgba(255,255,255,0.22)',
                                      color: 'white',
                                      fontWeight: 800,
                                      height: 20,
                                      fontSize: '0.68rem',
                                    }}
                                  />
                                )}
                              </Box>

                              <Grid container>
                                {DELIVERY_WINDOWS.map((win, idx) => {
                                  const zoneId = `${dateKey}-${win.key}`;
                                  const isHovered = activeDropZone === zoneId;

                                  // Find all drivers scheduled for this specific calendar date & window (filtered if driver filter is active)
                                  const scheduledDrivers = drivers.filter((drv) => {
                                    if (
                                      selectedDriverFilterIds.length > 0 &&
                                      !selectedDriverFilterIds.includes(drv.id)
                                    ) {
                                      return false;
                                    }
                                    const sched = schedules.find((s) => s.driverId === drv.id);
                                    const shiftStr = getScheduleShiftForDate(sched, dateObj);
                                    return parseShiftWindows(shiftStr).includes(win.key);
                                  });

                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={4}
                                      key={win.key}
                                      onDragOver={(e) => {
                                        if (!isAdmin) return;
                                        e.preventDefault();
                                        e.dataTransfer.dropEffect = 'copy';
                                        if (activeDropZone !== zoneId) {
                                          setActiveDropZone(zoneId);
                                        }
                                      }}
                                      onDragLeave={() => {
                                        if (!isAdmin) return;
                                        if (activeDropZone === zoneId) {
                                          setActiveDropZone(null);
                                        }
                                      }}
                                      onDrop={async (e) => {
                                        if (!isAdmin) return;
                                        e.preventDefault();
                                        setActiveDropZone(null);
                                        const droppedDriverId =
                                          e.dataTransfer.getData('text/plain') || draggingDriverId;
                                        if (droppedDriverId) {
                                          await assignDriverToDateWindow(
                                            droppedDriverId,
                                            dateObj,
                                            win.key
                                          );
                                        }
                                        setDraggingDriverId(null);
                                      }}
                                      sx={{
                                        p: { xs: 1.15, sm: 1.5 },
                                        minHeight: { xs: 96, sm: 120 },
                                        borderRight: {
                                          xs: 'none',
                                          sm: idx < 2 ? '1px solid' : 'none',
                                        },
                                        borderBottom: {
                                          xs: idx < 2 ? '1px solid' : 'none',
                                          sm: 'none',
                                        },
                                        borderColor: 'divider',
                                        bgcolor: isHovered
                                          ? 'rgba(25, 118, 210, 0.1)'
                                          : 'background.paper',
                                        outline: isHovered ? '2px dashed #1976d2' : 'none',
                                        outlineOffset: '-3px',
                                        transition: 'background-color 0.15s ease',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                      }}
                                    >
                                      <Box>
                                        <Box
                                          onClick={
                                            isAdmin
                                              ? (e) =>
                                                  setAddToShiftMenuAnchor({
                                                    el: e.currentTarget,
                                                    date: dateObj,
                                                    windowKey: win.key,
                                                  })
                                              : undefined
                                          }
                                          title={
                                            isAdmin
                                              ? `Click to add a driver to ${win.label} on this date`
                                              : undefined
                                          }
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            mb: 0.25,
                                            px: isAdmin ? 0.75 : 0,
                                            py: isAdmin ? 0.25 : 0,
                                            ml: isAdmin ? -0.75 : 0,
                                            borderRadius: 1,
                                            cursor: isAdmin ? 'pointer' : 'default',
                                            transition: 'background-color 0.15s ease',
                                            ...(isAdmin
                                              ? {
                                                  '&:hover': {
                                                    bgcolor: 'rgba(25, 118, 210, 0.1)',
                                                    '& .window-label-text': {
                                                      color: 'primary.main',
                                                      textDecoration: 'underline',
                                                    },
                                                  },
                                                }
                                              : {}),
                                          }}
                                        >
                                          {getWindowIcon(win.key)}
                                          <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            className="window-label-text"
                                          >
                                            {win.label}
                                          </Typography>
                                          {isAdmin && (
                                            <ArrowDropDownIcon
                                              fontSize="small"
                                              sx={{ color: 'text.secondary', ml: -0.5 }}
                                            />
                                          )}
                                        </Box>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block', mb: 1.5 }}
                                        >
                                          {win.timeRange}
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                          {scheduledDrivers.map((drv) => {
                                            const drvSched = schedules.find(
                                              (s) => s.driverId === drv.id
                                            );
                                            const isMarkedOff = isScheduleShiftMarkedOff(
                                              drvSched,
                                              dateObj,
                                              win.key
                                            );
                                            const drvColor = getDriverColor(drv.id, drivers);
                                            return (
                                              <Chip
                                                key={drv.id}
                                                label={
                                                  isMarkedOff ? `${drv.name} (off)` : drv.name
                                                }
                                                size="small"
                                                deleteIcon={
                                                  isAdmin ? <ArrowDropDownIcon /> : undefined
                                                }
                                                onClick={
                                                  isAdmin
                                                    ? (e) =>
                                                        setShiftMenuAnchor({
                                                          el: e.currentTarget,
                                                          driver: drv,
                                                          date: dateObj,
                                                          windowKey: win.key,
                                                        })
                                                    : undefined
                                                }
                                                onDelete={
                                                  isAdmin
                                                    ? (e) => {
                                                        const chipEl =
                                                          (e.currentTarget as HTMLElement).closest(
                                                            '.MuiChip-root'
                                                          ) as HTMLElement | null;
                                                        setShiftMenuAnchor({
                                                          el:
                                                            chipEl ||
                                                            (e.currentTarget as HTMLElement),
                                                          driver: drv,
                                                          date: dateObj,
                                                          windowKey: win.key,
                                                        });
                                                      }
                                                    : undefined
                                                }
                                                sx={{
                                                  fontWeight: 600,
                                                  cursor: isAdmin ? 'pointer' : 'default',
                                                  ...(isMarkedOff
                                                    ? {
                                                        bgcolor: 'grey.400',
                                                        color: 'grey.900',
                                                        '& .MuiChip-deleteIcon': {
                                                          color: 'grey.800',
                                                        },
                                                        '&:hover': {
                                                          bgcolor: isAdmin
                                                            ? 'grey.500'
                                                            : 'grey.400',
                                                        },
                                                      }
                                                    : {
                                                        bgcolor: drvColor.bg,
                                                        color: '#ffffff',
                                                        '& .MuiChip-deleteIcon': {
                                                          color: 'rgba(255,255,255,0.85)',
                                                          '&:hover': {
                                                            color: '#ffffff',
                                                          },
                                                        },
                                                        '&:hover': {
                                                          bgcolor: isAdmin
                                                            ? drvColor.hover
                                                            : drvColor.bg,
                                                        },
                                                      }),
                                                }}
                                              />
                                            );
                                          })}
                                        </Box>
                                      </Box>

                                      {scheduledDrivers.length === 0 && (
                                        <Box
                                          sx={{
                                            mt: 1,
                                            py: 1,
                                            px: 1,
                                            borderRadius: 1,
                                            border: '1px dashed',
                                            borderColor: isHovered ? 'primary.main' : 'grey.300',
                                            textAlign: 'center',
                                          }}
                                        >
                                          <Typography variant="caption" color="text.disabled">
                                            {isAdmin ? 'Drop driver here' : 'No drivers assigned'}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                </Grid>

                {/* Admin Shift Action Menu (Mark Off / Assign Month / Remove) */}
                <Menu
                  anchorEl={shiftMenuAnchor?.el || null}
                  open={Boolean(shiftMenuAnchor)}
                  onClose={() => setShiftMenuAnchor(null)}
                >
                  {shiftMenuAnchor && (() => {
                    const targetSched = schedules.find(
                      (s) => s.driverId === shiftMenuAnchor.driver.id
                    );
                    const currentlyOff = isScheduleShiftMarkedOff(
                      targetSched,
                      shiftMenuAnchor.date,
                      shiftMenuAnchor.windowKey
                    );
                    const winLabel =
                      DELIVERY_WINDOWS.find((w) => w.key === shiftMenuAnchor.windowKey)?.label ||
                      shiftMenuAnchor.windowKey;
                    const dateStr = formatCalendarDateLabel(shiftMenuAnchor.date);
                    const dowName = getDayOfWeekName(shiftMenuAnchor.date);
                    const monthLabel = shiftMenuAnchor.date.toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric',
                    });

                    return [
                      <MenuItem
                        key="toggle-off"
                        onClick={async () => {
                          const { driver, date, windowKey } = shiftMenuAnchor;
                          setShiftMenuAnchor(null);
                          await toggleDriverMarkedOffForDateShift(driver, date, windowKey);
                        }}
                      >
                        <ListItemIcon>
                          {currentlyOff ? (
                            <EventAvailableIcon fontSize="small" color="success" />
                          ) : (
                            <EventBusyIcon fontSize="small" color="action" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            currentlyOff
                              ? `Remove "(off)" Status for ${dateStr} (${winLabel})`
                              : `Mark "${shiftMenuAnchor.driver.name} (off)" for ${dateStr} (${winLabel})`
                          }
                        />
                      </MenuItem>,
                      <Divider key="div-month" />,
                      <MenuItem
                        key="assign-same-weekday-month"
                        onClick={async () => {
                          const { driver, date, windowKey } = shiftMenuAnchor;
                          setShiftMenuAnchor(null);
                          await applyMonthlyAssignmentForWeekdays({
                            driver,
                            year: date.getFullYear(),
                            monthIndex: date.getMonth(),
                            targetDays: [dowName],
                            targetWindows: [windowKey],
                            mode: 'assign',
                          });
                        }}
                      >
                        <ListItemIcon>
                          <DateRangeIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Assign to every ${dowName} (${winLabel}) in ${monthLabel}`}
                        />
                      </MenuItem>,
                      <MenuItem
                        key="assign-mon-fri-month"
                        onClick={async () => {
                          const { driver, date, windowKey } = shiftMenuAnchor;
                          setShiftMenuAnchor(null);
                          await applyMonthlyAssignmentForWeekdays({
                            driver,
                            year: date.getFullYear(),
                            monthIndex: date.getMonth(),
                            targetDays: [
                              'Monday',
                              'Tuesday',
                              'Wednesday',
                              'Thursday',
                              'Friday',
                            ],
                            targetWindows: [windowKey],
                            mode: 'assign',
                          });
                        }}
                      >
                        <ListItemIcon>
                          <CalendarMonthIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Assign Mon–Fri (${winLabel}) for entire ${monthLabel}`}
                        />
                      </MenuItem>,
                      <Divider key="div-rem" />,
                      <MenuItem
                        key="remove-shift"
                        onClick={async () => {
                          const { driver, date, windowKey } = shiftMenuAnchor;
                          setShiftMenuAnchor(null);
                          await removeDriverFromDateWindow(driver, date, windowKey);
                        }}
                      >
                        <ListItemIcon>
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Remove from ${dateStr} (${winLabel}) only`}
                          primaryTypographyProps={{ color: 'error.main' }}
                        />
                      </MenuItem>,
                      <MenuItem
                        key="remove-same-weekday-month"
                        onClick={async () => {
                          const { driver, date, windowKey } = shiftMenuAnchor;
                          setShiftMenuAnchor(null);
                          await applyMonthlyAssignmentForWeekdays({
                            driver,
                            year: date.getFullYear(),
                            monthIndex: date.getMonth(),
                            targetDays: [dowName],
                            targetWindows: [windowKey],
                            mode: 'remove',
                          });
                        }}
                      >
                        <ListItemIcon>
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Remove from every ${dowName} (${winLabel}) in ${monthLabel}`}
                          primaryTypographyProps={{ color: 'error.main' }}
                        />
                      </MenuItem>,
                    ];
                  })()}
                </Menu>

                {/* Admin Quick-Add Driver to Single Shift Menu (triggered by clicking Morning / Afternoon / Evening) */}
                <Menu
                  anchorEl={addToShiftMenuAnchor?.el || null}
                  open={Boolean(addToShiftMenuAnchor)}
                  onClose={() => setAddToShiftMenuAnchor(null)}
                >
                  {addToShiftMenuAnchor && (() => {
                    const { date, windowKey } = addToShiftMenuAnchor;
                    const winObj = DELIVERY_WINDOWS.find((w) => w.key === windowKey);
                    const dateLabel = formatCalendarDateLabel(date);

                    return [
                      <MenuItem key="header" disabled sx={{ opacity: '1 !important', py: 0.75 }}>
                        <ListItemText
                          primary={`Add Driver to ${winObj?.label || windowKey}`}
                          secondary={dateLabel}
                          primaryTypographyProps={{ fontWeight: 700, fontSize: '0.85rem', color: 'primary.main' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </MenuItem>,
                      <Divider key="div-top" />,
                      ...drivers.map((drv) => {
                        const sched = schedules.find((s) => s.driverId === drv.id);
                        const shiftStr = getScheduleShiftForDate(sched, date);
                        const alreadyAssigned = parseShiftWindows(shiftStr).includes(windowKey);
                        const drvColor = getDriverColor(drv.id, drivers);

                        return (
                          <MenuItem
                            key={drv.id}
                            disabled={alreadyAssigned}
                            onClick={async () => {
                              setAddToShiftMenuAnchor(null);
                              await assignDriverToDateWindow(drv.id, date, windowKey);
                            }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: drvColor.bg,
                                mr: 1.5,
                                flexShrink: 0,
                              }}
                            />
                            <ListItemText
                              primary={drv.name}
                              secondary={
                                alreadyAssigned ? 'Already assigned to this shift' : drv.role.toUpperCase()
                              }
                              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                              secondaryTypographyProps={{ fontSize: '0.72rem' }}
                            />
                          </MenuItem>
                        );
                      }),
                    ];
                  })()}
                </Menu>
              </Box>
            )}

            {activeTab === 3 && isAdmin && <QuizQuestionAdmin />}

            {/* SCHEDULE CHANGE LOGS (ADMIN ONLY) */}
            {activeTab === 2 && isAdmin && (
              <Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Driver Schedule Change Audit Logs (Admin Only)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Every schedule modification (assigning a driver, removing a driver, or marking a shift off/active) is automatically logged in the Firebase <code>scheduleLogs</code> collection along with the Administrator who made the change.
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.25 }}>
                  {scheduleLogs.map((log) => {
                    const actionColor =
                      log.action === 'Assigned'
                        ? 'success'
                        : log.action === 'Removed'
                        ? 'error'
                        : log.action === 'Marked Off'
                        ? 'default'
                        : 'primary';
                    return (
                      <Paper key={log.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Chip
                            size="small"
                            label={log.action}
                            color={actionColor}
                            sx={{ fontWeight: 700, flexShrink: 0 }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textAlign: 'right', lineHeight: 1.35 }}
                          >
                            {new Date(log.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={700}>
                          {log.driverName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {log.day}
                          {log.windowLabel ? ` · ${log.windowLabel}` : ''}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                        >
                          {log.details}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 1.25, overflowWrap: 'anywhere' }}
                        >
                          Changed by {log.adminName}
                          {log.adminEmail ? ` · ${log.adminEmail}` : ''}
                        </Typography>
                      </Paper>
                    );
                  })}
                  {scheduleLogs.length === 0 && (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No schedule changes logged yet. Any changes made to the Driver Schedule will appear here.
                    </Typography>
                  )}
                </Box>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 2, display: { xs: 'none', md: 'block' } }}
                >
                  <Table size="small">
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Changed By (Admin)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Driver</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Day & Shift Window</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scheduleLogs.map((log) => {
                        const actionColor =
                          log.action === 'Assigned'
                            ? 'success'
                            : log.action === 'Removed'
                            ? 'error'
                            : log.action === 'Marked Off'
                            ? 'default'
                            : 'primary';
                        return (
                          <TableRow key={log.id} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {new Date(log.timestamp).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {log.adminName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {log.adminEmail}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={log.action}
                                color={actionColor}
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{log.driverName}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {log.day}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {log.windowLabel}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: 220, overflowWrap: 'anywhere' }}>{log.details}</TableCell>
                          </TableRow>
                        );
                      })}
                      {scheduleLogs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            No schedule changes logged yet. Any changes made to the Driver Schedule will appear here.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </Paper>

        {isAdmin && (
          <Dialog
            open={clearScheduleOpen}
            onClose={() => {
              if (!clearingSchedule) setClearScheduleOpen(false);
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 700 }}>Clear every driver's schedule?</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary">
                This removes every Morning, Afternoon, and Evening assignment for every driver,
                including monthly schedules and marked-off days. The calendar will show everyone as
                off. This cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setClearScheduleOpen(false)} disabled={clearingSchedule}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={clearingSchedule}
                onClick={async () => {
                  setClearingSchedule(true);
                  const res = await clearAllDriverSchedules();
                  setClearingSchedule(false);
                  if (!res.success) {
                    setScheduleError(res.error || 'Could not clear the schedules.');
                    return;
                  }
                  setClearScheduleOpen(false);
                  setScheduleError(null);
                  setScheduleSuccess('Cleared the schedule for every driver.');
                }}
              >
                {clearingSchedule ? 'Clearing...' : 'Clear All Schedules'}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Add User / Driver Modal (Admin Only) */}
        {isAdmin && (
          <Dialog
            open={addModalOpen}
            onClose={() => setAddModalOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <Box component="form" onSubmit={handleAddAccount}>
              <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddAlt1Icon color="primary" /> Add New User / Driver
              </DialogTitle>
              <DialogContent dividers>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Full Name"
                  margin="dense"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="email"
                  label="Email Address"
                  margin="dense"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Number"
                  placeholder="(###) ###-####"
                  margin="dense"
                  value={newPhone}
                  onChange={(e) => setNewPhone(formatPhoneNumber(e.target.value))}
                />
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="password"
                  label="Initial Password (min 6 chars)"
                  margin="dense"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />

                <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                  <InputLabel>Account Role (Driver / Manager / Admin)</InputLabel>
                  <Select
                    label="Account Role (Driver / Manager / Admin)"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value as UserRole)}
                  >
                    <MenuItem value="driver">Driver</MenuItem>
                    <MenuItem value="manager">Manager</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mt: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block">
                    {newRole === 'admin' &&
                      'Admin: Full access to add, edit, and remove users, schedules, and view all data.'}
                    {newRole === 'manager' &&
                      'Manager: Can view all drivers, schedules, test scores, and logs, but CANNOT add, edit, or remove users or modify schedules.'}
                    {newRole === 'driver' &&
                      'Driver: Access to Driver Training and personal weekly schedule.'}
                  </Typography>
                </Paper>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setAddModalOpen(false)}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={creatingUser}>
                  {creatingUser ? 'Creating...' : 'Create User'}
                </Button>
              </DialogActions>
            </Box>
          </Dialog>
        )}

        {/* Edit User / Driver Modal (Admin Only) */}
        {isAdmin && (
          <Dialog
            open={Boolean(editingUser)}
            onClose={() => setEditingUser(null)}
            maxWidth="xs"
            fullWidth
          >
            <Box component="form" onSubmit={handleSaveUserEdit}>
              <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditOutlinedIcon color="primary" /> Edit User Details
              </DialogTitle>
              <DialogContent dividers>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Full Name"
                  margin="dense"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="email"
                  label="Email Address"
                  margin="dense"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Number"
                  placeholder="(###) ###-####"
                  margin="dense"
                  value={editPhone}
                  onChange={(e) => setEditPhone(formatPhoneNumber(e.target.value))}
                />
                <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                  <InputLabel>Role (Driver / Manager / Admin)</InputLabel>
                  <Select
                    label="Role (Driver / Manager / Admin)"
                    value={editRole}
                    disabled={editingUser?.id === currentUser.id}
                    onChange={(e) => setEditRole(e.target.value as UserRole)}
                  >
                    <MenuItem value="driver">Driver</MenuItem>
                    <MenuItem value="manager">Manager</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setEditingUser(null)}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={savingEdit}>
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogActions>
            </Box>
          </Dialog>
        )}

        {/* Monthly Bulk Assignment Modal (Admin Only) */}
        {isAdmin && (
          <Dialog
            open={bulkModalOpen}
            onClose={() => setBulkModalOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <Box component="form" onSubmit={handleBulkModalSubmit}>
              <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateRangeIcon color="primary" /> Assign Driver for Entire Month
              </DialogTitle>
              <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Assign (or remove) a driver for Morning, Afternoon, or Evening shifts across all matching days of the selected month.
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={7}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Driver</InputLabel>
                      <Select
                        label="Select Driver"
                        value={bulkDriverId}
                        onChange={(e) => setBulkDriverId(e.target.value)}
                      >
                        {drivers.map((drv) => (
                          <MenuItem key={drv.id} value={drv.id}>
                            {drv.name} ({drv.role.toUpperCase()})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      type="month"
                      size="small"
                      label="Target Month"
                      value={bulkMonthValue}
                      onChange={(e) => setBulkMonthValue(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    1. Action
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={bulkActionMode}
                    onChange={(_, val) => {
                      if (val) setBulkActionMode(val);
                    }}
                  >
                    <ToggleButton value="assign" sx={{ fontWeight: 700, px: 2 }}>
                      Assign to Shifts
                    </ToggleButton>
                    <ToggleButton value="remove" color="error" sx={{ fontWeight: 700, px: 2 }}>
                      Remove from Shifts
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box sx={{ mt: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    2. Select Shift Windows
                  </Typography>
                  <FormGroup row>
                    {DELIVERY_WINDOWS.map((win) => (
                      <FormControlLabel
                        key={win.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={bulkWindows[win.key]}
                            onChange={(e) =>
                              setBulkWindows((prev) => ({
                                ...prev,
                                [win.key]: e.target.checked,
                              }))
                            }
                          />
                        }
                        label={
                          <Typography variant="body2">
                            <strong>{win.label}</strong> ({win.timeRange})
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      3. Select Days of the Week in Month
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() =>
                          setBulkDays({
                            Monday: true,
                            Tuesday: true,
                            Wednesday: true,
                            Thursday: true,
                            Friday: true,
                            Saturday: false,
                            Sunday: false,
                          })
                        }
                        sx={{ fontSize: '0.75rem', py: 0.25 }}
                      >
                        Mon–Fri
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          setBulkDays({
                            Monday: true,
                            Tuesday: true,
                            Wednesday: true,
                            Thursday: true,
                            Friday: true,
                            Saturday: true,
                            Sunday: true,
                          })
                        }
                        sx={{ fontSize: '0.75rem', py: 0.25 }}
                      >
                        Every Day
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          setBulkDays({
                            Monday: false,
                            Tuesday: false,
                            Wednesday: false,
                            Thursday: false,
                            Friday: false,
                            Saturday: true,
                            Sunday: true,
                          })
                        }
                        sx={{ fontSize: '0.75rem', py: 0.25 }}
                      >
                        Weekends
                      </Button>
                    </Box>
                  </Box>

                  <FormGroup row>
                    {DAYS_OF_WEEK.map((day) => (
                      <FormControlLabel
                        key={day}
                        control={
                          <Checkbox
                            size="small"
                            checked={bulkDays[day]}
                            onChange={(e) =>
                              setBulkDays((prev) => ({
                                ...prev,
                                [day]: e.target.checked,
                              }))
                            }
                          />
                        }
                        label={<Typography variant="body2">{day.slice(0, 3)}</Typography>}
                      />
                    ))}
                  </FormGroup>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setBulkModalOpen(false)}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  color={bulkActionMode === 'remove' ? 'error' : 'primary'}
                  disabled={savingBulk}
                >
                  {savingBulk
                    ? 'Applying to Month...'
                    : bulkActionMode === 'assign'
                    ? 'Assign for Entire Month'
                    : 'Remove for Entire Month'}
                </Button>
              </DialogActions>
            </Box>
          </Dialog>
        )}

        {/* Back to Top Floating Button */}
        <Zoom in={showBackToTop}>
          <Fab
            color="primary"
            variant="extended"
            size="medium"
            onClick={scrollToTop}
            aria-label="Back to top"
            sx={{
              position: 'fixed',
              bottom: { xs: 20, sm: 28 },
              right: { xs: 20, sm: 32 },
              zIndex: 1200,
              fontWeight: 700,
              textTransform: 'none',
              px: 2.5,
              boxShadow: '0 6px 20px rgba(13, 71, 161, 0.35)',
              background: 'linear-gradient(90deg, #0D47A1 0%, #1976d2 100%)',
            }}
          >
            <KeyboardArrowUpIcon sx={{ mr: 0.5 }} />
            Back to Top
          </Fab>
        </Zoom>
      </Container>
    </Box>
  );
}
